import { doc, onSnapshot, type Unsubscribe } from 'firebase/firestore';
import {
  APP_VERSION_CONFIG_COLLECTION,
  TERMS_CONFIG_DOC_ID,
  TermsConfigSchema,
  type TermsConfig,
} from '@etiquetachef/types';
import { firestore } from './firebase';

const TAG = 'admin/termsConfig';

/** Estado do card da versão vigente dos Termos. */
export type TermsConfigResult =
  | { kind: 'LOADING' }
  /** `config: null` = o documento não existe (nenhuma versão vigente). */
  | { kind: 'READY'; config: TermsConfig | null }
  | { kind: 'ERROR'; error: Error };

/**
 * Observa `/appConfig/terms` (#679 9.13). Documento ausente é resposta, não
 * erro: é o estado "sem versão vigente" que a tela precisa mostrar em
 * destaque, porque nele o aceite da venda sai sem versão.
 *
 * Origem: #700 — commit 5fc292f3b, 2026-09-23 (versão vigente dos Termos
 * ganha escritor).
 *
 * Contrato: `READY` com `config` ou `null` (ausente OU fora do schema);
 * erro de snapshot → `ERROR`.
 *
 * Usado por: `routes/TermsVersionContainer.tsx`.
 *
 * Arquitetura: rule `match /appConfig/{platform}`; escrita só pela callable
 * `setTermsVersion`.
 *
 * Dados: `appConfig/terms` — schema `TermsConfigSchema` em `@etiquetachef/types`.
 *
 * Falhas conhecidas:
 *  - Doc fora do schema aparece como "sem versão vigente" e só vai para o
 *    `console.warn` — sem report, ninguém distingue "nunca publicado" de
 *    "publicado quebrado" (#926).
 *
 * Issues: #679 (9.13), #700.
 */
export function subscribeTermsConfig(onResult: (r: TermsConfigResult) => void): Unsubscribe {
  return onSnapshot(
    doc(firestore, APP_VERSION_CONFIG_COLLECTION, TERMS_CONFIG_DOC_ID),
    (snap) => {
      if (!snap.exists()) {
        onResult({ kind: 'READY', config: null });
        return;
      }
      const parsed = TermsConfigSchema.safeParse(snap.data());
      if (!parsed.success) {
        // Fora do schema vale como ausente: nenhum leitor do servidor o aceita.
        console.warn(`${TAG}: doc fora do schema`, parsed.error.issues);
        onResult({ kind: 'READY', config: null });
        return;
      }
      onResult({ kind: 'READY', config: parsed.data });
    },
    (err: Error) => {
      console.error(`${TAG}: erro ao observar`, err);
      onResult({ kind: 'ERROR', error: err });
    },
  );
}
